import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { HTMLAttributes } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { defer, sum } from 'lodash';

import { GroupTranslatorProvider, useGroupTranslator } from '../../../components/i18n/GroupTranslationContext';

// Utils
import { elementClassNames, getDomElementId } from '../../utils/elementUtil';
import { getBorderRenderedWidth } from './utils/domUtils/tableCellBorderMeasurementUtils';
import {
    getCaption,
    getElementId,
    getLabels,
    getReactions,
    getShowCaption,
    getShowTitle,
    getTableContentColWidthsGU,
    getWidth,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getElementStyle } from '../../../utils/grid/gridUtils';
import { getIsFeatureEnabled } from '../../feature/elementFeatureUtils';
import { getShowQuickLineTool } from '../../quickLine/quickLineUtil';
import { asObject, length } from '../../../../common/utils/immutableHelper';
import { parseCssPx } from '../../../utils/cssUtil';
import rafThrottle from '../../../../common/utils/lib/rafThrottle';
import { useActiveTableSelector } from '../bridge/store/hotTableStoreHooks';
import { selectActiveTableCellSelectionForElement } from '../bridge/store/selectors/activeHotTableCellSelectionSelectors';
import { selectFirstHotTableCell } from '../bridge/utils/hotInstanceUtils/selection/hotTableSelectionMutationUtils';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';
import { getMilanoteApplicationModeSelector } from '../../../platform/platformSelector';

// Components
import Caption from '../../../components/caption/Caption';
import QuickLineCreationTool from '../../quickLine/QuickLineCreationTool';
import PillsPanel from '../../pillsPanel/PillsPanel';
import AttachedCommentsDropTarget from '../../dnd/elementDropTargets/AttachedCommentsDropTarget';
import AttachedCommentsContainer from '../../attachedComment/AttachedCommentsContainer';
import ElementActivityIndicator from '../../activity/ElementActivityIndicator';
import ElementUserTag from '../../../user/userActivity/ElementUserTag';
import TableTitleEditor from './TableTitleEditor';
import PresentationalTable from './PresentationalTable';
import ElementResizeHandle, { ElementResizeHandleProps } from '../../resizing/ElementResizeHandle';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../../common/elements/elementConstants';
import { ELEMENT_FEATURES } from '../../feature/elementFeatureConstants';
import { MilanoteApplicationMode } from '../../../../common/platform/platformTypes';

// Types
import type { CaptionProps } from '../../../components/caption/captionComponentTypes';
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { Point } from '../../../../common/maths/geometry/pointTypes';
import type { CellSelection } from '../../../../common/table/TableTypes';
import type { TableTitleEditorProps } from './TableTitleEditor';

import './Table.scss';
import { MNHotInstance } from '../bridge/tableHotInstanceTypes';
import HotTable, { HotTableProps } from './HotTable';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';

const getIsTableEditingMobileEnabledForCurrentUser = getIsFeatureEnabledForCurrentUser(ExperimentId.tableEditingMobile);

type TableSpecificProps = {
    currentBoardId: string;
    element: ImMNElement;
    elementEvents?: HTMLAttributes<HTMLDivElement>;
    getContextZoomTranslationPx?: () => Point;
    inTrash?: boolean;
    isEditable?: boolean;
    isDragPreview?: boolean;
    isLocked?: boolean;
    isPresentational?: boolean;
    isSelected?: boolean;
    isSingleSelected?: boolean;
    permissions: number;
    setParentHoveredChildAcceptsDrop?: (acceptsDrop: boolean) => void;
    dispatchSetCellSelection: (
        elementId: string,
        cellSelection: CellSelection | null,
        selectionLayerLevel?: number,
        transactionId?: number,
    ) => void;
};

export type TableProps = HotTableProps &
    TableTitleEditorProps &
    CaptionProps &
    ElementResizeHandleProps &
    TableSpecificProps;

const TableContent: React.FC<TableProps> = (props) => {
    const groupTranslate = useGroupTranslator();
    const {
        element,
        gridSize,
        inList,
        isLocked,
        isEditable,
        elementEvents,
        isSelected,
        isSingleSelected,
        isDragPreview,
        isPresentational,
        currentBoardId,
        permissions,
        setParentHoveredChildAcceptsDrop,
        getContextZoomScale,
        getContextZoomTranslationPx,
        dispatchSetCellSelection,
    } = props;

    const tableRef = useRef<HTMLDivElement | null>(null);
    const tableTitleRef = useRef<HTMLDivElement | null>(null);
    const hotTableInstanceRef = useRef<MNHotInstance | null>(null);

    const elementId = getElementId(element);

    const applicationMode = useSelector(getMilanoteApplicationModeSelector);
    const isMobileTableEditingEnabled = useSelector(getIsTableEditingMobileEnabledForCurrentUser);

    const showTitle = getShowTitle(element);
    const showCaption = getShowCaption(element);
    const hasPills = length(getReactions(element)) > 0 || length(getLabels(element)) > 0;
    const showBottomPanel = showCaption || hasPills;
    const isTransparencyEnabled = getIsFeatureEnabled(ELEMENT_FEATURES.TRANSPARENT)(element);

    const captionContent = getCaption(element);
    const v2CellSelections = useActiveTableSelector(selectActiveTableCellSelectionForElement(elementId));

    const hotTableWidthGU = sum(asObject(getTableContentColWidthsGU(element)));
    const tableWidth = getWidth(element) || ELEMENT_DEFAULT_WIDTH;

    const transparent = isTransparencyEnabled;

    const isReadOnly =
        !isEditable || (applicationMode === MilanoteApplicationMode.mobile && !isMobileTableEditingEnabled);
    const showPresentational = !!isPresentational && !!document.querySelector(`.Table#el-${elementId}`);

    const removeElementBorder = !!v2CellSelections?.length;

    const tableClasses = useMemo(
        () =>
            classNames('Table drag-handle contrast-shadow', {
                'background-color-target': isSelected,
                'background-color-contrast-target': isSelected,
                'show-title': showTitle,
                'show-bottom-panel': showBottomPanel,
                'show-caption-and-pills': showCaption && hasPills,
                'remove-element-selection-border': removeElementBorder,
                'read-only': isReadOnly,
            }),
        [isReadOnly, isSelected, removeElementBorder, showBottomPanel, showTitle, showCaption, hasPills],
    );

    const classes = elementClassNames(tableClasses, { ...props, transparent });
    const showQuickLineTool = getShowQuickLineTool({
        isSingleSelected,
        element,
        inList,
        isEditable,
    });

    // When table is in column element, set table element width based on its container's width
    // instead of the table width
    const elementStyle = getElementStyle(tableWidth, gridSize, !!inList);

    const calculateTitleHeight = useCallback(() => {
        if (applicationMode !== MilanoteApplicationMode.desktop || !tableRef.current || !tableTitleRef.current) return;

        // nLines * 2.5rem (line height) + 3.5rem (padding) = total title height
        // if there is an even number of lines of text in the title, we will end up with 0.5rem off the grid points
        // we are adding this class to adjust the padding in this case
        const titleEditorElement = tableTitleRef.current.querySelector('.tiptap');
        const contentElement = titleEditorElement?.firstElementChild as HTMLElement | null;
        const titleRootElement = tableTitleRef.current.firstElementChild as HTMLElement | null;

        if (!contentElement || !titleRootElement) return;

        const contentHeight = parseCssPx(getComputedStyle(contentElement).height);
        const lineHeight = parseCssPx(getComputedStyle(contentElement).lineHeight);
        if (!lineHeight) return;

        const nLines = contentHeight / lineHeight;
        const isEven = nLines % 2 === 0;
        titleRootElement.classList.toggle('title-even-lines', isEven);

        const currentTitleHeight = parseCssPx(getComputedStyle(tableTitleRef.current).height) - 2;
        tableRef.current.style.setProperty('--table-title-height', `${currentTitleHeight}px`);
    }, [applicationMode]);

    useLayoutEffect(() => {
        if (!tableRef.current) return;

        if (showTitle) {
            tableRef.current.style.setProperty('--table-title-height', `calc(${5 * gridSize}px - 2px)`);
            calculateTitleHeight();
        } else {
            tableRef.current.style.setProperty('--table-title-height', '0px');
        }
    }, [calculateTitleHeight, gridSize, hotTableWidthGU, showTitle]);

    useEffect(() => {
        calculateTitleHeight();
    }, [calculateTitleHeight, gridSize]);

    const jumpToFirstCell = useCallback(() => {
        // We need to defer this to ensure the DOM's key events have been finished
        //  processing before triggering the cell selection, otherwise the table will start
        //  listening immediately and handle the event itself, resulting in another cell selection move
        defer(() => {
            selectFirstHotTableCell(hotTableInstanceRef.current);
        });
    }, [dispatchSetCellSelection, elementId]);

    useEffect(() => {
        if (!hotTableInstanceRef.current || !tableRef.current) return;

        const firstTdElement = hotTableInstanceRef.current.rootElement.querySelector('td');
        tableRef.current.style.setProperty(
            '--table-cell-border-rendered-width',
            `${getBorderRenderedWidth(firstTdElement)}px`,
        );
    }, []);

    useEffect(() => {
        if (!hotTableInstanceRef.current || !tableRef.current) return;

        const scrollElement = hotTableInstanceRef.current.rootElement.querySelector('.wtHolder') as HTMLElement | null;
        if (!scrollElement) return;

        const scrollBarHeight = scrollElement.offsetHeight - scrollElement.clientHeight;

        // This value should be 6px or 0px depending on whether the scrollbar is visible or not
        tableRef.current.style.setProperty('--scroll-bar-height', `${scrollBarHeight}px`);
    }, []);

    const isSelectedInGroup = !!isSelected && !isSingleSelected;
    const canResize = !isLocked && isEditable && !inList && !isReadOnly && !isSelectedInGroup;

    const throttledCalculateTitleHeight = useMemo(() => rafThrottle(calculateTitleHeight), [calculateTitleHeight]);

    return (
        <div id={getDomElementId(elementId)} ref={tableRef} className={classes} {...elementEvents} style={elementStyle}>
            {showTitle && (
                <TableTitleEditor
                    {...props}
                    ref={tableTitleRef}
                    onChange={throttledCalculateTitleHeight}
                    jumpToFirstCell={jumpToFirstCell}
                />
            )}
            {showPresentational && (
                <PresentationalTable
                    element={element}
                    gridSize={gridSize}
                    inList={inList}
                    isDragPreview={isDragPreview}
                />
            )}
            {!showPresentational && (
                <HotTable
                    {...props}
                    hotTableInstanceRef={hotTableInstanceRef}
                    showTitle={showTitle}
                    showCaption={showCaption}
                    showBottomPanel={showBottomPanel}
                    isReadOnly={isReadOnly}
                />
            )}
            <QuickLineCreationTool
                show={showQuickLineTool}
                elementId={elementId}
                element={element}
                currentBoardId={currentBoardId}
                gridSize={gridSize}
                getContextZoomScale={getContextZoomScale}
                getContextZoomTranslationPx={getContextZoomTranslationPx}
            />
            <ElementActivityIndicator {...props} />
            <ElementUserTag {...props} />
            <Caption
                {...props}
                padTop
                placeholder={groupTranslate('caption.placeholder')}
                textContent={captionContent}
                captionVisible={showCaption}
            />
            <PillsPanel {...props} />
            <AttachedCommentsDropTarget
                setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                gridSize={gridSize}
                isPresentational={isPresentational}
                element={element}
                elementId={elementId}
                getContextZoomScale={getContextZoomScale}
            />
            <AttachedCommentsContainer
                gridSize={gridSize}
                elementId={elementId}
                element={element}
                isPresentational={!!isPresentational}
                currentBoardId={currentBoardId}
                permissions={permissions}
            />
            <ElementResizeHandle {...props} showHandle={canResize} />
        </div>
    );
};

const Table: React.FC<TableProps> = (props) => {
    return (
        <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.table">
            <TableContent {...props} />
        </GroupTranslatorProvider>
    );
};

export default Table;
